'use client';

import { useState, useEffect } from 'react';
import { Navbar } from '../components/layout/navbar';
import { Footer } from '../components/layout/footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { ArticleCard } from '../components/blog/article-card';
import { Search, Filter, X } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';

// Direct imports
import { useArticles } from '../hooks/use-articles';
import { useTopics } from '../hooks/use-topics';
import { useSearch } from '../hooks/use-search';

export default function SearchPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('articles');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryParam = searchParams.get('q') || '';

  const filters = {
    search: searchQuery,
    topics: selectedTopics.length > 0 ? selectedTopics : undefined,
  };

  // Use hooks directly
  const { getAllArticlesQuery } = useArticles(filters);
  const { getAllTopicsQuery } = useTopics();
  const { searchResults, isSearching, performSearch } = useSearch();

  // Set initial search query from URL parameter
  useEffect(() => {
    if (queryParam) {
      setSearchQuery(queryParam);
      performSearch(queryParam);
    }
  }, [queryParam, performSearch]);

  // Get all topics for filters
  const { data: topics = [] } = getAllTopicsQuery;

  // Get articles with filters
  const { data: articles = [], isLoading: isLoadingArticles } =
    getAllArticlesQuery;

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(searchQuery);
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const toggleTopic = (topicSlug) => {
    setSelectedTopics((prev) =>
      prev.includes(topicSlug)
        ? prev.filter((t) => t !== topicSlug)
        : [...prev, topicSlug]
    );
  };

  const clearFilters = () => {
    setSelectedTopics([]);
  };

  return (
    <div className="flex min-h-screen flex-col bg-black text-white">
      {/* Gradient Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>

      <Navbar />
      <main className="flex-1 container px-4 py-8 md:px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-6">Search</h1>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="mb-8">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Search articles, topics, and authors..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={isSearching}>
                {isSearching ? 'Searching...' : 'Search'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-1"
              >
                <Filter className="h-4 w-4" />
                <span className="hidden sm:inline">Filters</span>
              </Button>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="mt-4 p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium">Filter by Topics</h3>
                  {selectedTopics.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="h-8 text-xs"
                    >
                      Clear All
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {topics.map((topic) => (
                    <Button
                      key={topic.slug}
                      variant={
                        selectedTopics.includes(topic.slug)
                          ? 'default'
                          : 'outline'
                      }
                      size="sm"
                      onClick={() => toggleTopic(topic.slug)}
                      className="h-8 text-xs"
                    >
                      {topic.name}
                      {selectedTopics.includes(topic.slug) && (
                        <X className="ml-1 h-3 w-3" />
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </form>

          {/* Search Results */}
          {searchQuery ? (
            <div>
              <h2 className="text-xl font-bold mb-4">
                Search Results for "{searchQuery}"
                {selectedTopics.length > 0 &&
                  ` (${selectedTopics.length} filters applied)`}
              </h2>

              <Tabs
                defaultValue={activeTab}
                onValueChange={setActiveTab}
                className="mb-6"
              >
                <TabsList>
                  <TabsTrigger value="articles">Articles</TabsTrigger>
                  <TabsTrigger value="topics">Topics</TabsTrigger>
                  <TabsTrigger value="authors">Authors</TabsTrigger>
                </TabsList>

                <TabsContent value="articles" className="mt-4">
                  {isSearching || isLoadingArticles ? (
                    <div className="space-y-6">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="rounded-lg border bg-card text-card-foreground shadow-sm"
                        >
                          <div className="flex flex-col md:flex-row">
                            <div className="md:w-1/3 aspect-video bg-muted animate-pulse" />
                            <div className="p-4 md:w-2/3">
                              <div className="h-4 bg-muted rounded animate-pulse mb-3 w-1/4" />
                              <div className="h-6 bg-muted rounded animate-pulse mb-2" />
                              <div className="h-4 bg-muted rounded animate-pulse mb-3 w-3/4" />
                              <div className="h-4 bg-muted rounded animate-pulse mb-3" />
                              <div className="h-4 bg-muted rounded animate-pulse w-1/2" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : searchResults.articles &&
                    searchResults.articles.length > 0 ? (
                    <div className="space-y-6">
                      {searchResults.articles.map((article) => (
                        <ArticleCard key={article.id} article={article} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 border rounded-lg">
                      <h3 className="text-xl font-bold mb-2">
                        No articles found
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        We couldn't find any articles matching "{searchQuery}".
                        {selectedTopics.length > 0 &&
                          ' Try removing some filters or '}
                        try a different search term.
                      </p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="topics" className="mt-4">
                  {isSearching ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="rounded-lg border p-4">
                          <div className="h-6 bg-muted rounded animate-pulse mb-2 w-1/3" />
                          <div className="h-4 bg-muted rounded animate-pulse w-2/3" />
                        </div>
                      ))}
                    </div>
                  ) : searchResults.topics &&
                    searchResults.topics.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {searchResults.topics.map((topic) => (
                        <div key={topic.slug} className="rounded-lg border p-4">
                          <h3 className="font-bold mb-1">{topic.name}</h3>
                          <p className="text-sm text-muted-foreground mb-2">
                            {topic.description}
                          </p>
                          <Button variant="outline" size="sm" asChild>
                            <a href={`/topics/${topic.slug}`}>View Topic</a>
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 border rounded-lg">
                      <h3 className="text-xl font-bold mb-2">
                        No topics found
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        We couldn't find any topics matching "{searchQuery}".
                        Try a different search term.
                      </p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="authors" className="mt-4">
                  {isSearching ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="rounded-lg border p-4 flex items-center gap-4"
                        >
                          <div className="h-12 w-12 rounded-full bg-muted animate-pulse" />
                          <div className="flex-1">
                            <div className="h-5 bg-muted rounded animate-pulse mb-2 w-1/3" />
                            <div className="h-4 bg-muted rounded animate-pulse w-2/3" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : searchResults.authors &&
                    searchResults.authors.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {searchResults.authors.map((author) => (
                        <div
                          key={author.id}
                          className="rounded-lg border p-4 flex items-center gap-4"
                        >
                          <img
                            src={author.avatar || '/placeholder.svg'}
                            alt={author.name}
                            loading="lazy"
                            className="h-12 w-12 rounded-full object-cover"
                          />
                          <div>
                            <h3 className="font-bold">{author.name}</h3>
                            <p className="text-sm text-muted-foreground">
                              {author.bio?.substring(0, 60)}...
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-2"
                              asChild
                            >
                              <a href={`/profile/${author.id}`}>View Profile</a>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 border rounded-lg">
                      <h3 className="text-xl font-bold mb-2">
                        No authors found
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        We couldn't find any authors matching "{searchQuery}".
                        Try a different search term.
                      </p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="text-center py-12 border rounded-lg">
              <h2 className="text-xl font-bold mb-2">Search for content</h2>
              <p className="text-muted-foreground mb-4">
                Enter a search term above to find articles, topics, and authors.
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
